import { useEffect, useLayoutEffect, useRef, type JSX } from 'react';
import * as THREE from 'three';
import { useStore } from '@/state/store';
import { buildSimulationMesh, type SimulationMesh } from '@/simulation/mesh';
import { createPaperScene, type CameraPreset, type SceneHandles } from '@/rendering/paperScene';

/**
 * 3D folded-model view.
 *
 * The scene is created once and driven imperatively. React only supplies the
 * canvas element and the settings; per-frame data goes straight from the store's
 * latest `frame` into Three.js buffers, so a 60 fps simulation does not cause
 * 60 React renders a second.
 */
export function FoldedViewer(): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<SceneHandles | null>(null);
  const meshRef = useRef<SimulationMesh | null>(null);
  const rafRef = useRef(0);
  const fpsRef = useRef({ frames: 0, last: performance.now() });

  const pattern = useStore((s) => s.pattern);
  const view = useStore((s) => s.view);
  const setFps = useStore((s) => s.setFps);
  const inspectEdge = useStore((s) => s.inspectEdge);
  const inspectVertex = useStore((s) => s.inspectVertex);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const scene = createPaperScene(canvas);
    sceneRef.current = scene;
    if (import.meta.env.DEV) {
      // Handy for poking at the scene graph from the console during development.
      (window as unknown as Record<string, unknown>).__fold = scene;
    }

    const observer = new ResizeObserver(() => {
      const rect = wrap.getBoundingClientRect();
      scene.resize(rect.width, rect.height);
    });
    observer.observe(wrap);
    const rect = wrap.getBoundingClientRect();
    scene.resize(rect.width, rect.height);

    const loop = (): void => {
      const frame = useStore.getState().frame;
      if (frame && meshRef.current && frame.positions.length === meshRef.current.vertexCount * 3) {
        scene.updatePositions(frame.positions, frame.triangleStrain, frame.collisionFlags);
      }
      scene.render();

      const now = performance.now();
      fpsRef.current.frames++;
      if (now - fpsRef.current.last >= 500) {
        setFps((fpsRef.current.frames * 1000) / (now - fpsRef.current.last));
        fpsRef.current.frames = 0;
        fpsRef.current.last = now;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      scene.dispose();
      sceneRef.current = null;
    };
  }, [setFps]);

  // Rebuild geometry whenever the topology changes.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const { mesh } = buildSimulationMesh(pattern);
    meshRef.current = mesh;
    scene.setMesh(mesh, pattern);
    scene.applyView(useStore.getState().view);
  }, [pattern]);

  useEffect(() => {
    sceneRef.current?.applyView(view);
  }, [view]);

  // Camera presets are dispatched through a custom event so that menu items and
  // keyboard shortcuts do not need a ref into this component.
  useEffect(() => {
    const handler = (e: Event): void => {
      const preset = (e as CustomEvent<CameraPreset>).detail;
      sceneRef.current?.setCameraPreset(preset);
    };
    window.addEventListener('origami-camera', handler);
    return () => window.removeEventListener('origami-camera', handler);
  }, []);

  const dragRef = useRef<{ vertex: number; plane: THREE.Plane } | null>(null);

  const ndcOf = (e: React.PointerEvent): THREE.Vector2 => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
  };

  /**
   * Dragging a vertex adds a positional pin to the compliant solver: the pinned
   * vertex is moved onto a plane through its own position facing the camera, and
   * the rest of the sheet has to accommodate it. That makes the app a hands-on
   * mechanism explorer rather than a slider-driven animation.
   */
  const onPointerMoveDrag = (e: React.PointerEvent): void => {
    const drag = dragRef.current;
    const scene = sceneRef.current;
    if (!drag || !scene) return;
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndcOf(e), scene.camera);
    const hit = new THREE.Vector3();
    if (!raycaster.ray.intersectPlane(drag.plane, hit)) return;
    const local = scene.toModelSpace(hit);
    useStore.getState().setPin({ index: drag.vertex, x: local.x, y: local.y, z: local.z });
  };

  const endDrag = (): void => {
    if (!dragRef.current) return;
    dragRef.current = null;
    sceneRef.current?.setControlsEnabled(true);
    useStore.getState().setPin(null);
  };

  const onPointerDown = (e: React.PointerEvent): void => {
    const scene = sceneRef.current;
    const mesh = meshRef.current;
    if (!scene || !mesh) return;
    const ndc = ndcOf(e);
    const hit = scene.pick(ndc);
    if (!hit) return;

    // Shift-drag grabs the paper. Compliant mode only - see the store comment.
    if (e.shiftKey && useStore.getState().sim.mode === 'compliant') {
      const normal = new THREE.Vector3();
      scene.camera.getWorldDirection(normal);
      dragRef.current = {
        vertex: hit.vertex,
        plane: new THREE.Plane().setFromNormalAndCoplanarPoint(normal, hit.worldPoint),
      };
      scene.setControlsEnabled(false);
      (e.target as Element).setPointerCapture?.(e.pointerId);
      return;
    }

    // Alt-click inspects the nearest crease; a plain click inspects the vertex.
    if (e.altKey) {
      const patternVertex = mesh.vertexPatternIndex[hit.vertex];
      const state = useStore.getState();
      let bestEdge = -1;
      for (let edge = 0; edge < state.pattern.edgesVertices.length; edge++) {
        const [a, b] = state.pattern.edgesVertices[edge];
        if (a === patternVertex || b === patternVertex) {
          if (state.pattern.edgesAssignment[edge] === 'M' || state.pattern.edgesAssignment[edge] === 'V') {
            bestEdge = edge;
            break;
          }
        }
      }
      if (bestEdge >= 0) inspectEdge(bestEdge);
      return;
    }
    inspectVertex(mesh.vertexPatternIndex[hit.vertex]);
    scene.highlight(hit.vertex);
  };

  return (
    <div ref={wrapRef} className="pane" style={{ flex: 1 }} data-testid="viewer-3d">
      <div className="pane-label">3D folded model</div>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMoveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      />
      <CameraButtons />
    </div>
  );
}

const PRESETS: { id: CameraPreset; label: string }[] = [
  { id: 'reset', label: 'Reset' },
  { id: 'front', label: 'Front' },
  { id: 'back', label: 'Back' },
  { id: 'top', label: 'Top' },
  { id: 'iso', label: 'Iso' },
];

function CameraButtons(): JSX.Element {
  const orthographic = useStore((s) => s.view.orthographic);
  const setView = useStore((s) => s.setView);
  return (
    <div style={{ position: 'absolute', right: 10, top: 8, display: 'flex', gap: 4 }}>
      {PRESETS.map((p) => (
        <button
          key={p.id}
          className="btn tiny"
          onClick={() =>
            window.dispatchEvent(new CustomEvent('origami-camera', { detail: p.id }))
          }
        >
          {p.label}
        </button>
      ))}
      <button
        className="btn tiny"
        aria-pressed={orthographic}
        onClick={() => setView({ orthographic: !orthographic })}
        title="Toggle orthographic projection"
      >
        {orthographic ? 'Ortho' : 'Persp'}
      </button>
    </div>
  );
}
